import client from './client';
import { Warehouse, WarehouseInventory } from '../types/entities';
import { ApiResponse, CreateWarehouseRequest } from '../types/api';

export const warehouseAPI = {
  getAll: async (filters?: { status?: string; search?: string }): Promise<Warehouse[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    
    const response = await client.get(`/warehouses?${params}`);
    return response.data.data;
  },

  getById: async (id: number): Promise<Warehouse> => {
    const response = await client.get(`/warehouses/${id}`);
    return response.data.data;
  },

  create: async (data: CreateWarehouseRequest): Promise<Warehouse> => {
    const response = await client.post('/warehouses', data);
    return response.data.data;
  },

  update: async (id: number, data: Partial<CreateWarehouseRequest>): Promise<Warehouse> => {
    const response = await client.put(`/warehouses/${id}`, data);
    return response.data.data;
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await client.delete(`/warehouses/${id}`);
    return response.data;
  },

  getInventory: async (warehouseId: number): Promise<WarehouseInventory[]> => {
    const response = await client.get(`/warehouses/${warehouseId}/inventory`);
    return response.data.data;
  },

  adjustInventory: async (
    warehouseId: number,
    data: { itemId: number; quantityIn?: number; quantityOut?: number; unit: string }
  ): Promise<WarehouseInventory> => {
    const response = await client.post(`/warehouses/${warehouseId}/inventory/adjust`, data);
    return response.data.data;
  },

  manualReceipt: async (
    warehouseId: number,
    data: {
      transactionDate?: string;
      supplierId?: number;
      transactionSubtype?: string;
      notes?: string;
      items: Array<{
        itemId?: number; // Optional - allows manual entry without selecting from autocomplete
        itemName: string;
        quantity: number;
        unit: string;
      }>;
    }
  ): Promise<any> => {
    const response = await client.post(`/warehouses/${warehouseId}/manual-receipt`, data);
    return response.data.data;
  },

  manualIssue: async (
    warehouseId: number,
    data: {
      transactionDate?: string;
      customerId?: number;
      notes?: string;
      items: Array<{
        itemId?: number; // Optional - allows manual entry without selecting from autocomplete
        itemName: string;
        quantity: number;
        unit: string;
      }>;
    }
  ): Promise<any> => {
    const response = await client.post(`/warehouses/${warehouseId}/manual-issue`, data);
    return response.data.data;
  },

  getFilteredInventory: async (
    warehouseId: number,
    filters?: {
      itemName?: string;
      unit?: string;
      stockStatus?: string;
      page?: number;
      pageSize?: number;
    }
  ): Promise<{
    data: WarehouseInventory[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const params = new URLSearchParams();
    if (filters?.itemName) params.append('itemName', filters.itemName);
    if (filters?.unit) params.append('unit', filters.unit);
    if (filters?.stockStatus) params.append('stockStatus', filters.stockStatus);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.pageSize) params.append('pageSize', filters.pageSize.toString());

    const response = await client.get(`/warehouses/${warehouseId}/inventory?${params}`);
    return response.data;
  },

  getItemHistory: async (
    warehouseId: number,
    itemId: number,
    filters?: {
      dateFrom?: string;
      dateTo?: string;
      transactionType?: string;
      supplierId?: number;
    }
  ): Promise<any> => {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.transactionType) params.append('transactionType', filters.transactionType);
    if (filters?.supplierId) params.append('supplierId', filters.supplierId.toString());

    const response = await client.get(
      `/warehouses/${warehouseId}/items/${itemId}/history?${params}`
    );
    return response.data.data;
  },

  downloadInventoryExport: async (
    warehouseId: number,
    filters?: {
      itemName?: string;
      unit?: string;
      stockStatus?: string;
    }
  ): Promise<void> => {
    const params = new URLSearchParams();
    if (filters?.itemName) params.append('itemName', filters.itemName);
    if (filters?.unit) params.append('unit', filters.unit);
    if (filters?.stockStatus) params.append('stockStatus', filters.stockStatus);

    const response = await client.get(`/warehouses/${warehouseId}/inventory/export?${params}`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ton-kho-${warehouseId}-${new Date().toISOString().split('T')[0]}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  },

  downloadItemHistoryExport: async (
    warehouseId: number,
    itemId: number,
    filters?: {
      dateFrom?: string;
      dateTo?: string;
      transactionType?: string;
      supplierId?: number;
    }
  ): Promise<void> => {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.transactionType) params.append('transactionType', filters.transactionType);
    if (filters?.supplierId) params.append('supplierId', filters.supplierId.toString());

    const response = await client.get(
      `/warehouses/${warehouseId}/items/${itemId}/history/export?${params}`,
      {
        responseType: 'blob',
      }
    );

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lich-su-item-${itemId}-${new Date().toISOString().split('T')[0]}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  },

  reconcile: async (
    warehouseId: number,
    itemId: number,
    data: { actualCount: number; reason?: string; notes?: string }
  ): Promise<any> => {
    const response = await client.post(
      `/warehouses/${warehouseId}/items/${itemId}/reconcile`,
      data
    );
    return response.data.data;
  },

  getReconciliationHistory: async (
    warehouseId: number,
    itemId: number
  ): Promise<any[]> => {
    const response = await client.get(
      `/warehouses/${warehouseId}/items/${itemId}/reconciliations`
    );
    return response.data.data;
  },

  // Stock Alert APIs
  getAlertSummary: async (): Promise<{
    lowStock: number;
    overstock: number;
    total: number;
  }> => {
    const response = await client.get('/warehouses/stock-alerts/summary');
    return response.data.data;
  },

  getActiveAlerts: async (filters?: {
    warehouseId?: number;
    alertType?: 'LOW_STOCK' | 'OVERSTOCK';
    acknowledged?: boolean;
  }): Promise<any[]> => {
    const response = await client.get('/warehouses/stock-alerts', {
      params: filters,
    });
    return response.data.data;
  },

  getThresholds: async (warehouseId: number): Promise<any[]> => {
    const response = await client.get(`/warehouses/stock-alerts/${warehouseId}/thresholds`);
    return response.data.data;
  },

  getThreshold: async (warehouseId: number, itemId: number): Promise<any> => {
    const response = await client.get(
      `/warehouses/stock-alerts/${warehouseId}/thresholds/${itemId}`
    );
    return response.data.data;
  },

  upsertThreshold: async (
    warehouseId: number,
    itemId: number,
    data: {
      minStockLevel?: number;
      maxStockLevel?: number;
      alertEnabled: boolean;
    }
  ): Promise<any> => {
    const response = await client.put(
      `/warehouses/stock-alerts/${warehouseId}/thresholds/${itemId}`,
      data
    );
    return response.data.data;
  },

  deleteThreshold: async (
    warehouseId: number,
    itemId: number
  ): Promise<void> => {
    await client.delete(`/warehouses/stock-alerts/${warehouseId}/thresholds/${itemId}`);
  },

  calculateThresholds: async (warehouseId: number): Promise<any[]> => {
    const response = await client.post(
      `/warehouses/stock-alerts/${warehouseId}/thresholds/auto-calculate`
    );
    return response.data.data;
  },

  applyAutoThresholds: async (
    warehouseId: number,
    overwriteManual: boolean = false
  ): Promise<{ appliedCount: number }> => {
    const response = await client.post(
      `/warehouses/stock-alerts/${warehouseId}/thresholds/apply-auto`,
      { overwriteManual }
    );
    return response.data.data;
  },

  acknowledgeAlert: async (
    warehouseId: number,
    itemId: number
  ): Promise<void> => {
    await client.post(
      `/warehouses/stock-alerts/${warehouseId}/${itemId}/acknowledge`
    );
  },

  // Period Lock APIs
  getPeriodLocks: async (filters?: {
    warehouseId?: number;
    status?: 'locked' | 'unlocked';
    year?: number;
    page?: number;
    pageSize?: number;
  }): Promise<{
    data: any[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const params = new URLSearchParams();
    if (filters?.warehouseId) params.append('warehouseId', filters.warehouseId.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.year) params.append('year', filters.year.toString());
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.pageSize) params.append('pageSize', filters.pageSize.toString());

    const response = await client.get(`/warehouses/period-locks?${params}`);
    return response.data;
  },

  getPeriodLockById: async (id: number): Promise<any> => {
    const response = await client.get(`/warehouses/period-locks/${id}`);
    return response.data.data;
  },

  createPeriodLock: async (data: {
    warehouseId: number;
    periodYear: number;
    periodMonth: number;
    lockReason: string;
  }): Promise<any> => {
    const response = await client.post('/warehouses/period-locks', data);
    return response.data.data;
  },

  unlockPeriod: async (lockId: number, data: { unlockReason: string }): Promise<any> => {
    const response = await client.put(`/warehouses/period-locks/${lockId}/unlock`, data);
    return response.data.data;
  },

  checkPeriodLockStatus: async (warehouseId: number, date: string): Promise<{
    isLocked: boolean;
    lockId?: number;
    periodLabel?: string;
    lockReason?: string;
  }> => {
    const response = await client.get(
      `/warehouses/${warehouseId}/period-lock-status?date=${date}`
    );
    return response.data.data;
  },

  // Stock Snapshot APIs
  getSnapshot: async (warehouseId: number, date: string): Promise<{
    warehouseId: number;
    warehouseName: string;
    warehouseCode: string;
    snapshotDate: string;
    items: Array<{
      itemId: number;
      itemName: string;
      unit: string;
      quantityIn: number;
      quantityOut: number;
      stockAtDate: number;
    }>;
    totalItems: number;
  }> => {
    const response = await client.get(
      `/warehouses/${warehouseId}/inventory/snapshot?date=${date}`
    );
    return response.data.data;
  },

  downloadSnapshotExport: async (warehouseId: number, date: string): Promise<void> => {
    const response = await client.get(
      `/warehouses/${warehouseId}/inventory/snapshot/export?date=${date}`,
      {
        responseType: 'blob',
      }
    );

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ton-kho-snapshot-${date}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  },

  // Audit Log APIs
  getAuditLogs: async (filters?: {
    warehouseId?: number;
    actionType?: string;
    actorId?: number;
    dateFrom?: string;
    dateTo?: string;
    itemId?: number;
    page?: number;
    pageSize?: number;
  }): Promise<{
    data: Array<{
      id: number;
      warehouseId: number | null;
      warehouseName?: string;
      warehouseCode?: string;
      itemId: number | null;
      itemName?: string;
      transactionId: number | null;
      actionType: string;
      actorId: number;
      actorName: string;
      actionTime: string;
      changesJson: any;
      ipAddress: string | null;
    }>;
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const params = new URLSearchParams();
    if (filters?.warehouseId) params.append('warehouseId', filters.warehouseId.toString());
    if (filters?.actionType) params.append('actionType', filters.actionType);
    if (filters?.actorId) params.append('actorId', filters.actorId.toString());
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.itemId) params.append('itemId', filters.itemId.toString());
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.pageSize) params.append('pageSize', filters.pageSize.toString());

    const response = await client.get(`/warehouses/audit-logs?${params}`);
    return response.data;
  },

  getAuditLogById: async (id: number): Promise<any> => {
    const response = await client.get(`/warehouses/audit-logs/${id}`);
    return response.data.data;
  },

  getAuditActionTypes: async (): Promise<string[]> => {
    const response = await client.get('/warehouses/audit-logs/action-types');
    return response.data.data;
  },

  downloadAuditLogExport: async (filters?: {
    warehouseId?: number;
    actionType?: string;
    actorId?: number;
    dateFrom?: string;
    dateTo?: string;
    itemId?: number;
  }): Promise<void> => {
    const params = new URLSearchParams();
    if (filters?.warehouseId) params.append('warehouseId', filters.warehouseId.toString());
    if (filters?.actionType) params.append('actionType', filters.actionType);
    if (filters?.actorId) params.append('actorId', filters.actorId.toString());
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.itemId) params.append('itemId', filters.itemId.toString());

    const response = await client.get(`/warehouses/audit-logs/export?${params}`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `nhat-ky-kho-${new Date().toISOString().split('T')[0]}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  },
};
